import { inject, Injectable } from "@angular/core";
import { CanActivate, Router, UrlTree } from "@angular/router";
import { AuthService } from "@app/auth.service";
import { MasterDataInitStatus, MasterDataService } from "@app/master-data.service";
import { ReadOnlyTokenStorage } from "@app/read-only-token.storage";
import { UserService } from "@domain/user/user.service";
import { environment } from "@environments/environment";
import { filter, first, map, Observable, of, switchMap } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class AppGuard implements CanActivate {
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private masterDataService = inject(MasterDataService);
  private router = inject(Router);
  private readOnlyTokenStorage = inject(ReadOnlyTokenStorage);

  canActivate(): Observable<boolean | UrlTree> {
    return this.authService.userInfo$.pipe(
      filter((userInfo) => !!userInfo.id || !!this.readOnlyTokenStorage.get()),
      first(),
      switchMap((userInfo) => {
        if (!userInfo.id && this.readOnlyTokenStorage.get()) {
          return this.initializeApplication();
        }

        const hasNoRights = !userInfo.rights || userInfo.rights.length === 0;
        const isInternal = userInfo.institutionType === "SC_INTERNAL" || userInfo.institutionId === environment.testInstitutionId;

        if (!userInfo.scope && !isInternal && hasNoRights && !!userInfo.institutionType) {
          return of(this.router.createUrlTree(["/external-user"]));
        } else if (!userInfo.scope && (isInternal || hasNoRights)) {
          return of(this.router.createUrlTree(["/scope-selection"]));
        }

        return this.initializeApplication();
      }),
    );
  }

  private initializeApplication(): Observable<boolean> {
    return this.userService.user$.pipe(
      first(),
      switchMap(() => {
        this.masterDataService.startPeriodicUpdates();

        return this.masterDataService.initStatus$.pipe(
          filter((status) => status === MasterDataInitStatus.SUCCESS),
          first(),
          map(() => true),
        );
      }),
    );
  }
}
